// react
import React, { useRef, useState } from 'react'

// external libs
import { Button, Grid, IconButton, styled } from '@material-ui/core'
import MuiAccordion from '@material-ui/core/Accordion'
import MuiAccordionDetails from '@material-ui/core/AccordionDetails'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import Typography from '@material-ui/core/Typography'
import { withStyles } from '@material-ui/core/styles'
import { FieldArray } from 'react-final-form-arrays'
import { useForm } from 'react-final-form'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

// components
import Dialog from '../../../../control/dialog/dialog'
import MyButton from '../../../../control/button/button'
import Tooltip from '../../../../control/tooltip/tooltip'
import UploadFile from './uploadFile/uploadFile'

// hooks
import useTranslate from '../../../../i18n/useTranslate'
import useZip from '../../shareLogic/useZip'
import useUploadDoc from './useUploadDoc'

// types
import type { ResearchDetailCtrlType, ResearchDetailViewType, UplodeFileObjType } from './uploadDocType'

type LinkProps = {
    title: string
    url: string
}
const Link: React.FC<LinkProps> = (props) => {
    const { title, url } = props
    const onClickLink = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (url !== '') {
            console.log('url : ', url)
            window.open(url, '_blank')
        }
    }
    return (
        <Typography
            component="span"
            style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={onClickLink}
        >
            {title}
        </Typography>
    )
}
const UploadDocView: React.FC<ResearchDetailViewType & { linkProps?: LinkProps | LinkProps[] }> = ({ ...props }) => {
    const translate = useTranslate()
    const {
        isExpanded,
        onClickAccordionChange,
        documentTitleList,
        isViewMode,
        initialData,
        title = translate.get('LABEL.UPLOAD_FILES'),
        name = 'uploadFile',
        disabledBtn = false,
        latestDocument,
        onClickOpenFile,
        required = true,
        canEditUploadFile = false,
        handleConfirm,
        handleCancel,
        linkProps,
        zipDownload = false,
        isRequire = true,
    } = props
    const form = useForm()
    const [isEditting, setIsEditting] = useState(false)
    const formStateRef = useRef<{ [key: string]: any }>({})
    const confirmRef = useRef<Record<string, Function>>()
    const cancelRef = useRef<Record<string, Function>>()
    const { generateZip } = useZip()
    const [isResetFileUpload, setIsResetFileUpload] = useState(false)
    const [stableInitialData, setStableInitialData] = useState(initialData)

    const createEmptyUploadItem = React.useCallback((topic: any) => {
        return {
            uploadFileObj: {
                fileList: [],
                subTopicPageList: [],
                documentTopicID: topic?.id,
                isDisplay: topic?.isDisplay,
                isRequired: topic?.isRequired,
            },
        }
    }, [])

    React.useEffect(() => {
        setStableInitialData(initialData ?? [])
    }, [initialData])

    React.useEffect(() => {
        if (!documentTitleList || documentTitleList.length === 0) return

        const currentUploadList = form.getFieldState(name)?.value ?? []
        const currentByTopic = new Map<string, any>()
        const initialByTopic = new Map<string, any>()

        currentUploadList.forEach((item: any) => {
            const topicID = item?.uploadFileObj?.documentTopicID
            if (topicID !== undefined && topicID !== null) {
                currentByTopic.set(String(topicID), item)
            }
        })

        ;(initialData ?? []).forEach((item: any) => {
            const topicID = item?.uploadFileObj?.documentTopicID
            if (topicID !== undefined && topicID !== null) {
                initialByTopic.set(String(topicID), item)
            }
        })

        const nextUploadList = documentTitleList.map((topic: any) => {
            const topicKey = String(topic?.id)
            const current = currentByTopic.get(topicKey)
            const initial = initialByTopic.get(topicKey)
            const fallback = initial ?? createEmptyUploadItem(topic)

            if (!current) return fallback

            return {
                ...fallback,
                ...current,
                uploadFileObj: {
                    ...fallback.uploadFileObj,
                    ...current.uploadFileObj,
                    documentTopicID: topic?.id,
                    isDisplay: topic?.isDisplay,
                    isRequired: topic?.isRequired,
                },
            }
        })

        const currentSignature = currentUploadList.map((item: any) => ({
            topicID: item?.uploadFileObj?.documentTopicID,
            fileCount: item?.uploadFileObj?.fileList?.length ?? 0,
            pageCount: item?.uploadFileObj?.subTopicPageList?.length ?? 0,
            isDisplay: item?.uploadFileObj?.isDisplay,
            isRequired: item?.uploadFileObj?.isRequired,
        }))
        const nextSignature = nextUploadList.map((item: any) => ({
            topicID: item?.uploadFileObj?.documentTopicID,
            fileCount: item?.uploadFileObj?.fileList?.length ?? 0,
            pageCount: item?.uploadFileObj?.subTopicPageList?.length ?? 0,
            isDisplay: item?.uploadFileObj?.isDisplay,
            isRequired: item?.uploadFileObj?.isRequired,
        }))
        const isDifferent = JSON.stringify(currentSignature) !== JSON.stringify(nextSignature)

        if (isDifferent) {
            form.change(name, nextUploadList)
        }
    }, [documentTitleList, form, name, initialData, createEmptyUploadItem])

    const fieldArrayKey = React.useMemo(() => {
        const topicSignature = JSON.stringify(
            (documentTitleList ?? []).map((item: any) => ({
                id: item?.id,
                no: item?.no,
                code: item?.code,
                isDisplay: item?.isDisplay,
                isRequired: item?.isRequired,
            })),
        )
        const initialSignature = JSON.stringify(
            (stableInitialData ?? []).map((item: any) => ({
                documentTopicID: item?.uploadFileObj?.documentTopicID,
                fileCount: item?.uploadFileObj?.fileList?.length ?? 0,
                pageCount: item?.uploadFileObj?.subTopicPageList?.length ?? 0,
            })),
        )

        return `${topicSignature}|${initialSignature}`
    }, [documentTitleList, stableInitialData])

    const renderUploadFile = (name: string, dataSource: any, topicID: number) => {
        let disabled = isViewMode && !isEditting

        return (
            <UploadFile
                name={`${name}.uploadFileObj`}
                dataSource={dataSource}
                label="Upload File"
                topicID={topicID}
                onChangeUpload={() => {}}
                subTitleList={dataSource?.subtopic ?? []}
                initialData={stableInitialData}
                disabled={disabled}
                disabledBtn={disabledBtn}
                latestDocument={latestDocument}
                required={required}
                onClickOpenFile={onClickOpenFile}
                canEditUploadFile={canEditUploadFile}
                resetLstFileUpload={isResetFileUpload}
            />
        )
    }

    const onClickEdit = (evt: any) => {
        onClickParent(evt)
        setIsEditting(true)
        formStateRef.current = form.getState().values
    }

    const onClickParent = (evt: any) => {
        evt.stopPropagation()
    }

    const onConfirm = (evt: any) => {
        onClickParent(evt)
        confirmRef?.current?.openDialog()
    }

    const onCancel = (evt: any) => {
        onClickParent(evt)
        cancelRef?.current?.openDialog()
    }

    const bindConfirmRef = (func: Record<string, Function>) => {
        confirmRef.current = { ...func }
    }

    const bindCancelRef = (func: Record<string, Function>) => {
        cancelRef.current = { ...func }
    }

    const onSubmitConfirm = () => {
        if (handleConfirm) {
            Promise.all([handleConfirm()]).then(() => setIsEditting(false))
        } else {
            setIsEditting(false)
        }
        confirmRef?.current?.closeDialog()
    }

    const onSubmitCancel = () => {
        form.change(name, formStateRef.current[name])
        setIsResetFileUpload(!isResetFileUpload)
        if (handleCancel) {
            Promise.all([handleCancel()]).then(() => setIsEditting(false))
        } else {
            setIsEditting(false)
        }
        cancelRef?.current?.closeDialog()
    }

    const onDownloadZip = async () => {
        const fileList = form.getFieldState(name)?.value ?? []
        let resultList: any[] = []
        if (fileList.length > 0) {
            fileList.forEach((data: UplodeFileObjType) => {
                if (data?.uploadFileObj?.fileList) {
                    resultList = [
                        ...resultList,
                        ...data.uploadFileObj.fileList.map((file) => ({
                            ...file,
                            documentTopicID: data.uploadFileObj?.documentTopicID,
                        })),
                    ]
                }
            })
        }
        const mappingResultNo = resultList.map((data, index) => {
            const foundData = documentTitleList.find(
                (titleData: any) => `${data.documentTopicID}` === `${titleData.id}`,
            )
            const customName = foundData?.code ? `${foundData?.no}_${foundData?.code}` : `${foundData?.no}`
            return {
                ...data,
                folderName: foundData ? customName : index + 1,
            }
        })
        generateZip(mappingResultNo)
    }

    return (
        <div>
            <Accordion
                square
                expanded={isExpanded}
                onChange={() => {
                    onClickAccordionChange(!isExpanded)
                }}
            >
                <AccordionSummary
                    aria-controls="panel1d-content"
                    id="panel1d-header"
                    expandIcon={<ExpandMoreIcon color={'primary'} />}
                >
                    <Grid container>
                        <Grid item xs={12}>
                            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                                {title}{' '}
                                {title === translate.get('LABEL.UPLOAD_FILES') && (
                                    <>
                                        <Typography component={'span'} style={{ fontWeight: 'bold', color: '#FF0000' }}>
                                            {isRequire
                                                ? `(${translate.get('LABEL.UPLOAD_FILES_DESCRIPTION')})`
                                                : `(${translate.get('LABEL.UPLOAD_FILES_DESCRIPTION')} ถ้ามี)`}
                                        </Typography>
                                        &nbsp;
                                        {zipDownload && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                onClick={(evt) => {
                                                    onClickParent(evt)
                                                    onDownloadZip()
                                                }}
                                            >
                                                {translate.get('LABEL.DOWNLOAD_DOC')}
                                            </Button>
                                        )}
                                    </>
                                )}
                                {canEditUploadFile && (
                                    <div style={{ marginLeft: '10px', float: 'right' }}>
                                        {!isEditting ? (
                                            <Tooltip title={translate.get('BUTTON.EDIT')}>
                                                <IconButton size="small" onClick={onClickEdit}>
                                                    <EditOutlinedIcon color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <>
                                                &nbsp;
                                                <CancelBtn color="secondary" onClick={onCancel}>
                                                    {translate.get('BUTTON.CANCEL')}
                                                </CancelBtn>
                                                &nbsp;
                                                <ConfirmBtn color="primary" onClick={onConfirm}>
                                                    {translate.get('BUTTON.CONFIRM')}
                                                </ConfirmBtn>
                                            </>
                                        )}
                                    </div>
                                )}
                                {linkProps && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {(Array.isArray(linkProps) ? linkProps : [linkProps]).map((link, index) => (
                                            <Link key={`${link.title}-${index}`} {...link} />
                                        ))}
                                    </div>
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container spacing={2}>
                        <FieldArray key={fieldArrayKey} name={name}>
                            {(
                                { fields }, //loop from initialData
                            ) =>
                                fields.map((name, index) => {
                                    const uploadFileObj = form.getFieldState(`${name}.uploadFileObj`)?.value
                                    const topicID = uploadFileObj?.documentTopicID
                                    const hasTopicID = topicID !== undefined && topicID !== null
                                    const topicData = hasTopicID
                                        ? documentTitleList?.find((doc: any) => `${doc.id}` === `${topicID}`)
                                        : documentTitleList?.[index]

                                    if (!topicData) {
                                        return null
                                    }

                                    return (
                                        <Grid container item xs={12} key={name}>
                                            <Grid container item xs={12} justify={'flex-end'}>
                                                {renderUploadFile(name, topicData, topicData.id)}
                                            </Grid>
                                        </Grid>
                                    )
                                })
                            }
                        </FieldArray>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Dialog
                onRef={bindConfirmRef}
                type="alert-confirm"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={translate.get('ALERT.CONTENT.NEED_TO_SAVE')}
                onClickConfirm={onSubmitConfirm}
            />
            <Dialog
                onRef={bindCancelRef}
                type="alert-confirm"
                title={translate.get('ALERT.TITLE.NOTIFICATION')}
                content={translate.get('ALERT.CONTENT.NEED_TO_CANCEL')}
                onClickConfirm={onSubmitCancel}
            />
        </div>
    )
}

let UploadDocCtrl: React.FC<ResearchDetailCtrlType & { linkProps?: LinkProps | LinkProps[] }> = ({ ...props }) => {
    const uploadDocModel = useUploadDoc({ ...props })
    return <UploadDocView {...uploadDocModel} {...props} />
}

export default UploadDocCtrl

const ConfirmBtn = styled(MyButton)({
    /** Confirm Style */
})

const CancelBtn = styled(MyButton)({
    /** Cancel Style */
})

const Accordion = withStyles({
    root: {
        border: '1px solid rgba(0, 0, 0, .125)',
        boxShadow: 'none',
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&:before': {
            display: 'none',
        },
        '&$expanded': {
            margin: 'auto',
        },
    },
    expanded: {},
})(MuiAccordion)

const AccordionSummary = withStyles({
    root: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid rgba(0, 0, 0, .125)',
        marginBottom: -1,
        minHeight: 56,
        '&$expanded': {
            minHeight: 56,
        },
    },
    content: {
        '&$expanded': {
            margin: '12px 0',
        },
    },
    expanded: {},
})(MuiAccordionSummary)

const AccordionDetails = withStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiAccordionDetails)
